import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useCallback, useEffect } from "react";
import { toast } from "sonner";

const STORED_KEYS_LS_KEY = "agentframer:api-keys";

function loadStoredKeys(): Record<string, string> {
	try {
		return JSON.parse(localStorage.getItem(STORED_KEYS_LS_KEY) ?? "{}") as Record<string, string>;
	} catch {
		return {};
	}
}

function saveStoredKey(keyId: string, value: string) {
	const stored = loadStoredKeys();
	stored[keyId] = value;
	localStorage.setItem(STORED_KEYS_LS_KEY, JSON.stringify(stored));
}

function removeStoredKey(keyId: string) {
	const stored = loadStoredKeys();
	delete stored[keyId];
	localStorage.setItem(STORED_KEYS_LS_KEY, JSON.stringify(stored));
}
import {
	DashboardLayout,
} from "@/components/dashboard/DashboardLayout";
import { useSession } from "@/hooks/auth-hooks";

export const Route = createFileRoute("/dashboard/api-keys")({
	component: ApiKeysPage,
});

// ─── Types ────────────────────────────────────────────────────────────────────

type McpKeySummary = {
	createdAt?: string | null;
	configured: boolean;
	id?: string | null;
	keyPrefix: string | null;
	lastUsedAt: string | null;
	name: string | null;
	updatedAt: string | null;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatRelativeTime(dateValue: string | null | undefined): string {
	if (!dateValue) return "Never";
	const diff = Date.now() - new Date(dateValue).getTime();
	if (diff < 60_000) return "Just now";
	if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
	if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
	return `${Math.floor(diff / 86_400_000)}d ago`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function CopyButton({ text, className }: { text: string; className?: string }) {
	const [copied, setCopied] = useState(false);

	const handleCopy = useCallback(() => {
		navigator.clipboard.writeText(text).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		});
	}, [text]);

	return (
		<button
			type="button"
			onClick={handleCopy}
			className={`focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60 ${className ?? ""}`}
			aria-label="Copy to clipboard"
		>
			{copied ? (
				<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "18px", color: "#2b9a66" }}>check</span>
			) : (
				<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "18px", color: "var(--km-on-surface-muted)" }}>content_copy</span>
			)}
		</button>
	);
}

function StatusBadge({ configured }: { configured: boolean }) {
	if (configured) {
		return (
			<span
				className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.1em]"
				style={{ fontFamily: "inherit", color: "#6B3FF2" }}
			>
				<span className="w-1.5 h-1.5 rounded-full bg-[#6B3FF2] animate-pulse" />
				Active
			</span>
		);
	}
	return (
		<span
			className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide"
			style={{ fontFamily: "inherit", color: "var(--km-on-surface-muted)" }}
		>
			<span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "rgba(255,255,255,0.08)" }} />
			Inactive
		</span>
	);
}

function KeyHashCell({ keyPrefix }: { keyPrefix: string | null }) {
	const [copied, setCopied] = useState(false);

	const handleCopy = useCallback(() => {
		if (!keyPrefix) return;
		navigator.clipboard.writeText(keyPrefix).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		});
	}, [keyPrefix]);

	return (
		<button
			type="button"
			onClick={handleCopy}
			aria-label={copied ? "Key hash copied" : `Copy key hash ${keyPrefix ?? ""} to clipboard`}
			className="group/hash flex items-center gap-2 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
			disabled={!keyPrefix}
		>
			<span
				className="text-sm"
				style={{ fontFamily: "'Geist Mono', 'JetBrains Mono', monospace", color: "var(--km-on-surface-muted)" }}
			>
				{keyPrefix ? `${keyPrefix}••••••••` : "-"}
			</span>
			{keyPrefix && (
				<span
					className="opacity-0 group-hover/row:opacity-100 transition-opacity"
					style={{ color: copied ? "#2b9a66" : "var(--km-on-surface-muted)" }}
				>
					<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px" }}>
						{copied ? "check" : "content_copy"}
					</span>
				</span>
			)}
		</button>
	);
}

function KeyRow({
	keyData,
	onRevoke,
	revoking,
	fullKey,
	onDismissKey,
}: {
	keyData: McpKeySummary;
	onRevoke: () => void;
	revoking: boolean;
	fullKey?: string;
	onDismissKey?: () => void;
}) {
	return (
		<tr
			className="group/row transition-colors"
			style={{ backgroundColor: fullKey ? "rgba(43,154,102,0.03)" : "var(--km-surface-low)" }}
			onMouseEnter={e => { if (!fullKey) (e.currentTarget as HTMLElement).style.backgroundColor = "var(--km-surface-high)"; }}
			onMouseLeave={e => { if (!fullKey) (e.currentTarget as HTMLElement).style.backgroundColor = "var(--km-surface-low)"; }}
		>
			<td
				className="py-4 px-4 font-bold"
				style={{ fontFamily: "'Geist', -apple-system, sans-serif", color: "var(--km-on-surface)" }}
			>
				{keyData.name ?? "Default Key"}
			</td>
			<td className="py-4 px-4">
				{fullKey ? (
					<div className="flex items-center gap-2">
						<code
							className="text-xs break-all select-all"
							style={{ fontFamily: "'Geist Mono', 'JetBrains Mono', monospace", color: "#2b9a66" }}
						>
							{fullKey}
						</code>
						<CopyButton text={fullKey} className="shrink-0" />
					</div>
				) : (
					<KeyHashCell keyPrefix={keyData.keyPrefix} />
				)}
			</td>
			<td className="py-4 px-4">
				<span
					className="text-xs"
					style={{ fontFamily: "inherit", color: "var(--km-on-surface-muted)" }}
				>
					{formatRelativeTime(keyData.lastUsedAt)}
				</span>
			</td>
			<td className="py-4 px-4">
				<StatusBadge configured={keyData.configured} />
			</td>
			<td className="py-4 px-4 text-right">
				<div className="flex items-center justify-end gap-1">
					{fullKey ? (
						<button
							type="button"
							onClick={onDismissKey}
							className="flex items-center justify-center size-8 transition-colors hover:text-[var(--km-on-surface)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
							style={{ color: "var(--km-on-surface-muted)" }}
							aria-label="Dismiss"
						>
							<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px" }}>close</span>
						</button>
					) : (
						<button
							type="button"
							onClick={onRevoke}
							disabled={revoking}
							className="flex items-center justify-center size-8 transition-colors hover:text-[#ff6e84] disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff6e84]/60"
							style={{ color: "var(--km-on-surface-muted)" }}
							aria-label="Revoke key"
						>
							<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "18px" }}>
								{revoking ? "hourglass_empty" : "delete"}
							</span>
						</button>
					)}
				</div>
			</td>
		</tr>
	);
}

function CreateKeyForm({
	onCancel,
	onCreate,
	isPending,
}: {
	onCancel: () => void;
	onCreate: (name: string) => void;
	isPending: boolean;
}) {
	const [name, setName] = useState("");

	const handleSubmit = useCallback(() => {
		onCreate(name.trim());
	}, [name, onCreate]);

	return (
		<div
			className="p-4 mb-6"
			style={{ backgroundColor: "rgba(234,40,4,0.04)", border: "1px solid #6B3FF2", borderTop: "3px solid #6B3FF2" }}
		>
			<p
				className="text-xs font-bold uppercase tracking-[0.1em] mb-3"
				style={{ fontFamily: "inherit", color: "#6B3FF2" }}
			>
				Create new API key
			</p>
			<div className="flex items-center gap-3">
				<input
					type="text"
					value={name}
					onChange={(e) => setName(e.target.value)}
					placeholder="e.g. Claude Desktop"
					aria-label="API key name"
					className="flex-1 h-9 px-3 text-sm placeholder:text-[var(--km-on-surface-muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
					style={{
						backgroundColor: "var(--km-surface)",
						border: "1px solid rgba(255,255,255,0.08)",
						color: "var(--km-on-surface)",
						fontFamily: "inherit",
					}}
					onKeyDown={(e) => {
						if (e.key === "Enter") handleSubmit();
						if (e.key === "Escape") onCancel();
					}}
					// biome-ignore lint/a11y/noAutofocus: intentional for inline form
					autoFocus
				/>
				<button
					type="button"
					onClick={handleSubmit}
					disabled={isPending}
					aria-busy={isPending}
					className="h-9 px-5 text-xs font-bold uppercase tracking-[0.1em] transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
					style={{
						fontFamily: "inherit",
						backgroundColor: "#6B3FF2",
						color: "#ffffff",
					}}
				>
					{isPending ? "Creating…" : "Create"}
				</button>
				<button
					type="button"
					onClick={onCancel}
					className="h-9 px-4 text-xs font-bold uppercase tracking-wide transition-colors hover:text-[var(--km-on-surface)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
					style={{
						fontFamily: "inherit",
						border: "1px solid rgba(255,255,255,0.08)",
						color: "var(--km-on-surface-muted)",
					}}
				>
					Cancel
				</button>
			</div>
		</div>
	);
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function ApiKeysPage() {
	useSession();

	const queryClient = useQueryClient();
	const [showCreateForm, setShowCreateForm] = useState(false);
	const [storedKeys, setStoredKeys] = useState<Record<string, string>>({});

	useEffect(() => {
		setStoredKeys(loadStoredKeys());
	}, []);

	const { data, isLoading, isError } = useQuery({
		queryKey: ["dashboard", "mcp-key"],
		queryFn: async () => {
			const res = await fetch("/api/mcp/key");
			if (!res.ok) throw new Error("Failed to fetch API key");
			return res.json() as Promise<{ key: McpKeySummary; keys: McpKeySummary[] }>;
		},
	});

	const createMutation = useMutation({
		mutationFn: async (name: string) => {
			const res = await fetch("/api/mcp/key", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ name }),
			});
			if (!res.ok) throw new Error("Failed to create API key");
			return res.json() as Promise<{ apiKey: string; key: McpKeySummary; keys: McpKeySummary[] }>;
		},
		onSuccess: (result) => {
			if (result.key.id) {
				saveStoredKey(result.key.id, result.apiKey);
				setStoredKeys(loadStoredKeys());
			}
			setShowCreateForm(false);
			queryClient.invalidateQueries({ queryKey: ["dashboard", "mcp-key"] });
		},
		onError: () => {
			toast.error("Failed to create API key. Please try again.");
		},
	});

	const revokeMutation = useMutation({
		mutationFn: async (keyId: string | undefined) => {
			const res = await fetch("/api/mcp/key", {
				method: "DELETE",
				headers: { "Content-Type": "application/json" },
				body: keyId ? JSON.stringify({ keyId }) : JSON.stringify({}),
			});
			if (!res.ok) throw new Error("Failed to revoke API key");
		},
		onSuccess: (_data, keyId) => {
			if (keyId) {
				removeStoredKey(keyId);
				setStoredKeys(loadStoredKeys());
			}
			queryClient.invalidateQueries({ queryKey: ["dashboard", "mcp-key"] });
			toast.success("Key revoked");
		},
		onError: () => {
			toast.error("Failed to revoke API key. Please try again.");
		},
	});

	const handleRevoke = useCallback((keyId?: string) => {
		if (
			window.confirm(
				"Are you sure you want to revoke this API key? This action cannot be undone.",
			)
		) {
			revokeMutation.mutate(keyId);
		}
	}, [revokeMutation]);

	const handleCreate = useCallback(
		(name: string) => {
			createMutation.mutate(name);
		},
		[createMutation],
	);

	const keys = data?.keys ?? [];

	return (
		<DashboardLayout>
			{/* Header */}
				<div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
					<div>
						<p
							className="text-[11px] font-bold uppercase tracking-[0.1em] mb-2"
							style={{ fontFamily: "inherit", color: "#6B3FF2" }}
						>
							Dashboard
						</p>
						<h1
							className="text-5xl font-semibold tracking-tighter mb-2"
							style={{ fontFamily: "'Geist', -apple-system, sans-serif" }}
						>
							API Keys
						</h1>
						<p
							className="text-xs uppercase tracking-wide font-bold"
							style={{ fontFamily: "inherit", color: "var(--km-on-surface-muted)" }}
						>
							Manage secure programmatic access to AgentFramer
						</p>
					</div>
					<div
						className="hidden lg:block h-px flex-1 mx-8 mb-4"
						style={{
							background: "linear-gradient(to right, rgba(234,40,4,0.3), transparent)",
						}}
					/>
				</div>

				{/* Inline create form */}
				{showCreateForm && (
					<CreateKeyForm
						onCancel={() => setShowCreateForm(false)}
						onCreate={handleCreate}
						isPending={createMutation.isPending}
					/>
				)}

				{/* Keys card */}
				<div
					className="relative p-8 overflow-hidden"
					style={{ backgroundColor: "var(--km-surface-low)", border: "1px solid rgba(255,255,255,0.08)", borderTop: "3px solid #6B3FF2" }}
				>
					{/* Decorative background icon */}
					<div className="absolute top-0 right-0 p-4 opacity-[0.07] pointer-events-none">
						<span className="material-symbols-outlined text-[#6B3FF2]" aria-hidden="true" style={{ fontSize: "120px" }}>
							key
						</span>
					</div>

					{/* Section header */}
					<div
						className="flex justify-between items-end mb-8 pb-6 relative z-10"
						style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
					>
						<div>
							<h2
								className="text-xl font-semibold tracking-tight"
								style={{ fontFamily: "'Geist', -apple-system, sans-serif" }}
							>
								API Access Keys
							</h2>
							<p className="text-sm mt-1" style={{ color: "var(--km-on-surface-muted)" }}>
								Manage secure programmatic access to AgentFramer services.
							</p>
						</div>
						{!showCreateForm && (
							<button
								type="button"
								onClick={() => setShowCreateForm(true)}
								className="flex items-center gap-1.5 px-6 py-3 text-xs font-bold uppercase tracking-wide transition-all hover:opacity-90 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
								style={{
									fontFamily: "inherit",
									backgroundColor: "#6B3FF2",
									color: "#ffffff",
								}}
							>
								<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "14px" }}>add</span>
								Create New Key
							</button>
						)}
					</div>

					{isLoading && (
						<div className="space-y-3 relative z-10">
							<div className="h-4 w-32 rounded-none animate-pulse" style={{ backgroundColor: "var(--km-surface-high)" }} />
							<div className="h-12 w-full rounded-none animate-pulse" style={{ backgroundColor: "var(--km-surface-high)" }} />
							<div className="h-12 w-full rounded-none animate-pulse" style={{ backgroundColor: "var(--km-surface-high)" }} />
						</div>
					)}

					{isError && !isLoading && (
						<div
							className="flex items-center justify-center py-12 text-sm relative z-10"
							style={{ color: "var(--km-on-surface-muted)" }}
						>
							Failed to load API keys. Please refresh the page.
						</div>
					)}

					{!isLoading && !isError && keys.length > 0 && (
						<div className="overflow-x-auto relative z-10">
							<table
								className="w-full text-left border-separate"
								style={{ borderSpacing: "0 12px" }}
							>
								<thead>
									<tr>
										{["Key Name", "Key Hash", "Last Used", "Status", "Actions"].map((h, i) => (
											<th
												key={h}
												scope="col"
												className="pb-2 font-bold px-4 text-[10px] uppercase tracking-wide"
												style={{
													fontFamily: "inherit",
													color: "var(--km-on-surface-muted)",
													textAlign: i === 4 ? "right" : "left",
												}}
											>
												{h}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{keys.map((entry) => (
										<KeyRow
											key={entry.id ?? entry.keyPrefix ?? entry.name ?? "mcp-key"}
											keyData={entry}
											onRevoke={() => handleRevoke(entry.id ?? undefined)}
											revoking={revokeMutation.isPending}
											fullKey={entry.id ? storedKeys[entry.id] : undefined}
											onDismissKey={() => {
												if (entry.id) {
													removeStoredKey(entry.id);
													setStoredKeys(loadStoredKeys());
												}
											}}
										/>
									))}
								</tbody>
							</table>
						</div>
					)}

					{!isLoading && !isError && keys.length === 0 && (
						<div className="flex flex-col items-center justify-center py-16 gap-5 relative z-10">
							<div
								className="flex items-center justify-center w-16 h-16"
								style={{ backgroundColor: "rgba(234,40,4,0.1)", border: "1px solid #6B3FF2" }}
							>
								<span className="material-symbols-outlined text-[#6B3FF2]" aria-hidden="true" style={{ fontSize: "32px" }}>
									terminal
								</span>
							</div>
							<div className="text-center">
								<p
									className="font-semibold"
									style={{ fontFamily: "'Geist', -apple-system, sans-serif", color: "var(--km-on-surface)" }}
								>
									No API key configured
								</p>
								<p
									className="text-xs mt-1"
									style={{ fontFamily: "inherit", color: "var(--km-on-surface-muted)" }}
								>
									Create an API key to access AgentFramer programmatically.
								</p>
							</div>
							{!showCreateForm && (
								<button
									type="button"
									onClick={() => setShowCreateForm(true)}
									className="flex items-center gap-2 px-6 py-3 text-xs font-bold uppercase tracking-wide transition-all hover:opacity-90 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
									style={{
										fontFamily: "inherit",
										backgroundColor: "#6B3FF2",
										color: "#ffffff",
									}}
								>
									<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "14px" }}>add</span>
									Create New Key
								</button>
							)}
						</div>
					)}
				</div>

				{/* Stats mini-cards */}
				<div className="grid grid-cols-2 gap-4 mt-8">
					<div
						className="p-6"
						style={{ backgroundColor: "var(--km-surface-low)", border: "1px solid rgba(255,255,255,0.08)", borderTop: "3px solid #2b9a66" }}
					>
						<span
							className="text-[10px] uppercase tracking-[0.1em] font-bold"
							style={{ fontFamily: "inherit", color: "#2b9a66" }}
						>
							Uptime
						</span>
						<div
							className="text-2xl font-semibold mt-1"
							style={{ fontFamily: "'Geist Mono', 'JetBrains Mono', monospace", color: "#2b9a66" }}
						>
							99.9%
						</div>
					</div>
					<div
						className="p-6"
						style={{ backgroundColor: "var(--km-surface-low)", border: "1px solid rgba(255,255,255,0.08)", borderTop: "3px solid #6B3FF2" }}
					>
						<span
							className="text-[10px] uppercase tracking-[0.1em] font-bold"
							style={{ fontFamily: "inherit", color: "#6B3FF2" }}
						>
							Latency
						</span>
						<div
							className="text-2xl font-semibold mt-1"
							style={{ fontFamily: "'Geist Mono', 'JetBrains Mono', monospace", color: "#6B3FF2" }}
						>
							24ms
						</div>
					</div>
				</div>
		</DashboardLayout>
	);
}
